import { Fragment, FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, type AccessCode, type Redemption } from '@/services/adminApi';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export function AccessCodesPage(): JSX.Element {
  const { t } = useTranslation();
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);

  const [createLabel, setCreateLabel] = useState('');
  const [createCode, setCreateCode] = useState('');
  const [createRecruiter, setCreateRecruiter] = useState(false);
  const [createFreeToUse, setCreateFreeToUse] = useState(false);
  const [createCommissionPct, setCreateCommissionPct] = useState('15');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [redemptions, setRedemptions] = useState<Record<string, Redemption[] | 'loading' | 'error'>>({});

  const refresh = useCallback(async () => {
    setStatus('loading');
    setLoadError(null);
    try {
      const { codes } = await adminApi.listAccessCodes();
      setCodes(codes);
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      setLoadError(err instanceof Error ? err.message : t('admin.accessCodes.loadError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setCreateBusy(true);
    setCreateError(null);
    try {
      const body: { label?: string; code?: string; recruiterGrant?: boolean; freeToUse?: boolean; commissionRate?: number } = {};
      const label = createLabel.trim();
      const code = createCode.trim();
      if (label.length) body.label = label;
      if (code.length) body.code = code;
      if (createRecruiter) {
        body.recruiterGrant = true;
        const pct = Number(createCommissionPct);
        if (Number.isFinite(pct)) body.commissionRate = Math.min(1, Math.max(0, pct / 100));
      }
      if (createFreeToUse) body.freeToUse = true;
      await adminApi.createAccessCode(body);
      setCreateLabel('');
      setCreateCode('');
      setCreateRecruiter(false);
      setCreateFreeToUse(false);
      setCreateCommissionPct('15');
      await refresh();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t('admin.accessCodes.createError'));
    } finally {
      setCreateBusy(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    setPendingDeleteId(id);
    try {
      await adminApi.deleteAccessCode(id);
      await refresh();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('admin.accessCodes.deleteError'));
    } finally {
      setPendingDeleteId(null);
    }
  }

  async function handleToggleRedemptions(id: string): Promise<void> {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (redemptions[id] && redemptions[id] !== 'error') return; // cached
    setRedemptions((prev) => ({ ...prev, [id]: 'loading' }));
    try {
      const { redemptions: rows } = await adminApi.listRedemptions(id);
      setRedemptions((prev) => ({ ...prev, [id]: rows }));
    } catch {
      setRedemptions((prev) => ({ ...prev, [id]: 'error' }));
    }
  }

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('admin.accessCodes.title')}</h1>
        <p className="muted">
          {t('admin.accessCodes.blurb')}
        </p>
      </header>

      <section className="admin-card">
        <h2 className="admin-card__title">{t('admin.accessCodes.createTitle')}</h2>
        <form className="admin-create-form" onSubmit={handleCreate} noValidate>
          <div className="field">
            <label htmlFor="ac-label">{t('admin.accessCodes.labelField')}</label>
            <input
              id="ac-label"
              type="text"
              value={createLabel}
              onChange={(e) => setCreateLabel(e.target.value)}
              maxLength={120}
              placeholder={t('admin.accessCodes.labelPlaceholder')}
            />
          </div>
          <div className="field">
            <label htmlFor="ac-code">{t('admin.accessCodes.codeField')}</label>
            <input
              id="ac-code"
              type="text"
              value={createCode}
              onChange={(e) => setCreateCode(e.target.value)}
              maxLength={64}
              placeholder={t('admin.accessCodes.codePlaceholder')}
              autoCapitalize="characters"
              spellCheck={false}
            />
          </div>
          <div className="field">
            <span>{t('admin.accessCodes.specialPermissions')}</span>
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={createRecruiter}
                onChange={(e) => setCreateRecruiter(e.target.checked)}
              />
              {t('admin.accessCodes.recruiterPermission')}
            </label>
            {createRecruiter && (
              <div className="field" style={{ marginTop: 8 }}>
                <label htmlFor="ac-commission">{t('admin.recruiters.codeCommission.label')}</label>
                <input
                  id="ac-commission"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={createCommissionPct}
                  onChange={(e) => setCreateCommissionPct(e.target.value)}
                  style={{ width: 72 }}
                />
                <p className="muted">{t('admin.recruiters.codeCommission.hint')}</p>
              </div>
            )}
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={createFreeToUse}
                onChange={(e) => setCreateFreeToUse(e.target.checked)}
              />
              {t('admin.accessCodes.freeToUsePermission')}
            </label>
          </div>
          <button type="submit" className="btn" disabled={createBusy}>
            {createBusy ? t('admin.accessCodes.creating') : t('admin.accessCodes.createButton')}
          </button>
          {createError && (
            <p className="error" role="alert" style={{ marginTop: 12 }}>
              {createError}
            </p>
          )}
        </form>
      </section>

      <section className="admin-card">
        <h2 className="admin-card__title">{t('admin.accessCodes.allCodes')}</h2>
        {status === 'loading' && (
          <p className="muted" role="status">{t('common.loading')}</p>
        )}
        {status === 'error' && (
          <p className="error" role="alert">{loadError ?? t('admin.accessCodes.loadError')}</p>
        )}
        {status === 'ready' && codes.length === 0 && (
          <p className="muted">{t('admin.accessCodes.empty')}</p>
        )}
        {status === 'ready' && codes.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.accessCodes.colCode')}</th>
                  <th scope="col">{t('admin.accessCodes.colLabel')}</th>
                  <th scope="col" className="num">{t('admin.accessCodes.colRedemptions')}</th>
                  <th scope="col" className="num">{t('admin.accessCodes.colUniqueUsers')}</th>
                  <th scope="col" className="num">{t('admin.accessCodes.colUniqueClients')}</th>
                  <th scope="col">{t('admin.accessCodes.colLastRedeemed')}</th>
                  <th scope="col" aria-label={t('admin.common.actions')} />
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => {
                  const isExpanded = expandedId === c.id;
                  const rs = redemptions[c.id];
                  return (
                    <Fragment key={c.id}>
                      <tr>
                        <td>
                          <code>{c.code}</code>
                          <div>
                            {c.recruiterGrant && <span className="code-badge">{t('admin.accessCodes.badgeRecruiter')}</span>}
                            {c.freeToUse && <span className="code-badge">{t('admin.accessCodes.badgeFree')}</span>}
                            {c.ownerUserId && <span className="code-badge">{t('admin.accessCodes.badgeReferral')}</span>}
                          </div>
                        </td>
                        <td>{c.label ?? <span className="text-dim">—</span>}</td>
                        <td className="num">{c.redemptionCount}</td>
                        <td className="num">{c.uniqueUserCount}</td>
                        <td className="num">{c.uniqueClientCount}</td>
                        <td>{c.lastRedeemedAt ? formatDate(c.lastRedeemedAt) : <span className="text-dim">{t('common.relativeTime.never')}</span>}</td>
                        <td className="actions">
                          <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => void handleToggleRedemptions(c.id)}
                            aria-expanded={isExpanded}
                            aria-controls={`redemptions-${c.id}`}
                          >
                            {isExpanded ? t('admin.accessCodes.hide') : t('admin.accessCodes.view')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => void handleDelete(c.id)}
                            disabled={pendingDeleteId === c.id}
                            aria-label={t('admin.accessCodes.deleteAria', { code: c.code })}
                          >
                            {pendingDeleteId === c.id ? t('admin.accessCodes.deleting') : t('admin.accessCodes.delete')}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr id={`redemptions-${c.id}`} className="admin-redemptions-row">
                          <td colSpan={7}>
                            {rs === 'loading' && <p className="muted">{t('admin.accessCodes.loadingRedemptions')}</p>}
                            {rs === 'error' && <p className="error" role="alert">{t('admin.accessCodes.redemptionsError')}</p>}
                            {Array.isArray(rs) && rs.length === 0 && (
                              <p className="muted">{t('admin.accessCodes.noRedemptions')}</p>
                            )}
                            {Array.isArray(rs) && rs.length > 0 && (
                              <table className="admin-table admin-table--inner">
                                <thead>
                                  <tr>
                                    <th scope="col">{t('admin.accessCodes.colWhen')}</th>
                                    <th scope="col">{t('admin.accessCodes.colUser')}</th>
                                    <th scope="col">{t('admin.accessCodes.colIp')}</th>
                                    <th scope="col">{t('admin.accessCodes.colUserAgent')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rs.map((r) => (
                                    <tr key={r.id}>
                                      <td>{formatDate(r.redeemedAt)}</td>
                                      <td>{r.userId ?? <span className="text-dim">{t('admin.accessCodes.guest')}</span>}</td>
                                      <td><code>{r.ipAddress}</code></td>
                                      <td className="user-agent">{r.userAgent || <span className="text-dim">—</span>}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
